import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type {
  QrReviewBatch,
  QrReviewMetrics,
  QrReviewListParams,
  QrReviewPageRecord,
  RejectSessionParams,
  SubmitVerdictParams,
  UpdatePageQrScanParams
} from '../types';

const { QR_REVIEW } = API_ENDPOINTS;

export const qrReviewApi = {
  getList: (params?: QrReviewListParams) =>
    apiClient.getPaginated<QrReviewBatch>(QR_REVIEW.LIST, params),

  getMetrics: () => apiClient.get<QrReviewMetrics>(QR_REVIEW.METRICS),

  getById: (id: number) => apiClient.get<QrReviewBatch>(QR_REVIEW.SHOW(id)),

  startReview: (id: number) =>
    apiClient.post<QrReviewBatch>(QR_REVIEW.START_REVIEW(id), {}),

  endSession: (id: number) =>
    apiClient.post<QrReviewBatch>(QR_REVIEW.END_SESSION(id), {}),

  submitVerdict: ({ batchId, pageId, verdict }: SubmitVerdictParams) =>
    apiClient.post(QR_REVIEW.SUBMIT_VERDICT(batchId, pageId), { verdict }),

  rejectSession: ({
    batchId,
    target_stage,
    rejection_reason_code,
    notes
  }: RejectSessionParams) =>
    apiClient.post<QrReviewBatch>(QR_REVIEW.REJECT_SESSION(batchId), {
      target_stage,
      rejection_reason_code,
      notes
    }),

  updatePageQrScan: ({
    batchId,
    pageId,
    barcode,
    qr_bbox
  }: UpdatePageQrScanParams) =>
    apiClient.put(QR_REVIEW.UPDATE_PAGE_QR_SCAN(batchId, pageId), {
      barcode,
      qr_bbox
    }),

  /**
   * Upload an operator-enhanced page image (brightness/contrast/grayscale/
   * sharpen baked in client-side). Routed through `apiClient.uploadFormData`
   * so the transfer emits byte-level progress via `onProgress`. Reuses the
   * photo-scanning enhance endpoint from `API_ENDPOINTS` (config, not a
   * cross-feature import).
   */
  enhancePage: (
    batchId: number,
    pageId: number,
    imageBlob: Blob,
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    formData.append('image', imageBlob, 'enhanced.jpg');
    return apiClient.uploadFormData<QrReviewPageRecord>(
      API_ENDPOINTS.PHOTO_SCANNING.ENHANCE_PAGE(batchId, pageId),
      formData,
      onProgress
    );
  }
};
